/* 
 * DESIGN: "Leśna Przystań" — Forest Deep background, Ember accents
 * Sticky top price bar + navigation with dropdown for groups
 */
import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X, Phone, Tag, ChevronDown, Shield } from "lucide-react";

const LOGO_URL = "/manus-storage/logo_4d2e8d83.webp";

const navLinks = [
  { href: "/", label: "Główna" },
  { href: "/splyw", label: "Spływy" },
  { href: "/cennik", label: "Cennik" },
  {
    label: "Grupy & Eventy",
    href: "/grupy-i-eventy",
    children: [
      { href: "/grupy-i-eventy#firmowe", label: "👔 Integracje firmowe" },
      { href: "/grupy-i-eventy#panienski", label: "💍 Wieczór panieński" },
      { href: "/grupy-i-eventy#urodziny", label: "🎂 Urodziny" },
      { href: "/grupy-i-eventy#szkola", label: "🎓 Wycieczki szkolne" },
      { href: "/grupy-i-eventy#rodzina", label: "👨‍👩‍👧 Rodziny" },
    ],
  },
  { href: "/z-psem", label: "🐾 Z psem" },
  { href: "/ognisko", label: "🔥 Ognisko" },
  { href: "/galeria", label: "Galeria" },
  { href: "/o-nas", label: "O nas" },
  { href: "/kontakt", label: "Kontakt" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [priceBarHeight, setPriceBarHeight] = useState(40);
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const priceBarRef = useRef<HTMLDivElement>(null);
  const [location] = useLocation();

  const openDropdown = () => {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
    setDropdownOpen(true);
  };

  const closeDropdown = () => {
    dropdownTimeout.current = setTimeout(() => setDropdownOpen(false), 180);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const updateBarHeight = () => {
      if (priceBarRef.current) {
        setPriceBarHeight(priceBarRef.current.offsetHeight);
      }
    };
    updateBarHeight();
    window.addEventListener("resize", updateBarHeight);
    return () => window.removeEventListener("resize", updateBarHeight);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setDropdownOpen(false);
  }, [location]);

  return (
    <>
      {/* TOP PRICE BAR — Main USP */}
      <div className="sticky-price-bar" ref={priceBarRef}>
        <div className="flex items-center justify-center gap-2 text-sm" style={{ flexWrap: "nowrap", overflow: "hidden" }}>
          <Shield size={14} className="flex-shrink-0" />
          <span className="font-bold truncate">
            <span className="hidden sm:inline">🏆 GWARANCJA NAJNIŻSZEJ CENY NAD PILICĄ — Znajdziesz taniej? Damy Ci <strong>5% taniej!</strong></span>
            <span className="sm:hidden">🏆 Gwarancja najniższej ceny nad Pilicą</span>
          </span>
        </div>
      </div>

      {/* MAIN NAVBAR */}
      <nav
        className={`fixed left-0 right-0 z-[150] transition-all duration-300 ${
          scrolled
            ? "shadow-lg shadow-black/20"
            : ""
        }`}
        style={{ background: "oklch(0.28 0.08 155)", top: `${priceBarHeight}px` }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <img
                src={LOGO_URL}
                alt="Kajaki Kudłaty Cooky"
                loading="eager"
                decoding="async"
                className="h-8 w-8 sm:h-10 sm:w-10 object-contain"
              />
              <div>
                <div
                  className="font-bold text-white leading-tight"
                  style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(0.8rem, 3.2vw, 1.1rem)", whiteSpace: "nowrap" }}
                >
                  Kajaki Kudłaty Cooky
                </div>
                <div
                  className="hidden sm:block text-[oklch(0.78_0.12_70)] leading-tight"
                  style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.15em", textTransform: "uppercase" }}
                >
                  Zakościele · Pilica
                </div>
              </div>
            </Link>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-stretch gap-0.5 min-w-0 flex-1 justify-center">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={link.label} className="relative flex items-center"
                    onMouseEnter={openDropdown}
                    onMouseLeave={closeDropdown}
                  >
                    <Link
                      href={link.href!}
                      aria-expanded={dropdownOpen}
                      aria-haspopup="true"
                      className={`inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                        location.startsWith("/grupy")
                          ? "text-[oklch(0.78_0.12_70)]"
                          : "text-white/90 hover:text-[oklch(0.78_0.12_70)]"
                      }`}
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      {link.label}
                      <ChevronDown size={13} className={`transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} />
                    </Link>
                    {dropdownOpen && (
                      <>
                        {/* Niewidoczny mostek — zapobiega zamknięciu menu przy przejściu kursorem */}
                        <div
                          className="absolute top-full left-0 w-56 h-2"
                          onMouseEnter={openDropdown}
                          onMouseLeave={closeDropdown}
                        />
                        <div
                          className="absolute top-full left-0 w-56 rounded-xl shadow-2xl py-2 z-50 border"
                          style={{ background: "oklch(0.22 0.07 155)", borderColor: "oklch(1 0 0 / 0.08)", marginTop: "0.5rem" }}
                          onMouseEnter={openDropdown}
                          onMouseLeave={closeDropdown}
                        >
                        {link.children.map((child) => (
                          <Link
                            key={child.href}
                            href={child.href}
                            className="block px-4 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                            style={{ fontFamily: "'Outfit', sans-serif" }}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                      </>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.href}
                    href={link.href}
                    className={`inline-flex items-center px-3 py-2 rounded text-sm font-medium transition-colors ${
                      location === link.href
                        ? "text-[oklch(0.78_0.12_70)]"
                        : "text-white/90 hover:text-[oklch(0.78_0.12_70)]"
                    }`}
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {link.label}
                  </Link>
                )
              )}
            </div>

            {/* CTA + Mobile toggle */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <a
                href="tel:+48787028770"
                className="hidden md:flex items-center gap-2 transition-all text-sm font-bold hover:opacity-80"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  color: "oklch(0.88 0.18 72)",
                  letterSpacing: "0.02em"
                }}
              >
                <Phone size={15} strokeWidth={2.5} />
                787 028 770
              </a>
              <Link
                href="/rezerwacja"
                className="btn-ember text-sm py-2 px-4 hidden sm:inline-flex"
              >
                Zarezerwuj
              </Link>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="lg:hidden text-white p-2 rounded hover:bg-white/10 transition-colors"
                aria-label="Menu"
                aria-expanded={isOpen}
              >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <div
            className="lg:hidden border-t border-white/10"
            style={{ background: "oklch(0.24 0.07 155)", maxHeight: "calc(100dvh - 104px)", overflowY: "auto" }}
          >
            <div className="px-4 py-3 space-y-1">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={link.label}>
                    <div
                      className="px-4 pt-3 pb-1 text-[10px] font-semibold text-white/40 uppercase tracking-widest"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {link.label}
                    </div>
                    {link.children.map((child) => (
                      <Link
                        key={child.href}
                        href={child.href}
                        className="block px-6 py-2.5 rounded-lg text-sm text-white/80 hover:bg-white/10 transition-colors"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={link.href}
                    href={link.href}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      location === link.href
                        ? "bg-[oklch(0.62_0.18_45)] text-white"
                        : "text-white/90 hover:bg-white/10"
                    }`}
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {link.label}
                  </Link>
                )
              )}
              <div className="pt-3 pb-1 border-t border-white/10 flex flex-col gap-2">
                <a
                  href="tel:+48787028770"
                  className="flex items-center gap-2 px-4 py-3 text-white/90 hover:bg-white/10 rounded-lg text-sm font-medium"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  <Phone size={16} />
                  787 028 770
                </a>
                <Link
                  href="/rezerwacja"
                  className="btn-ember w-full justify-center py-3"
                >
                  Zarezerwuj spływ
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
      {/* Spacer to push content below fixed bar + nav */}
      <div style={{ height: `${priceBarHeight + 64}px` }} />
    </>
  );
}
